import React, { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { 
  AlertTriangle, 
  RefreshCw, 
  Search, 
  Trash2,
  CheckSquare,
  Square,
  Calendar,
  User,
  FileText,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { adminApi } from '../services/adminApi';
import { UploadJob } from '../types';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const FailedUploads: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUploads, setSelectedUploads] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();

  const { data: uploadsData, isLoading, error } = useQuery({
    queryKey: ['failed-uploads', currentPage, searchTerm],
    queryFn: () => adminApi.getFailedUploads({
      page: currentPage,
      limit: 20,
      search: searchTerm || undefined,
    }),
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const bulkRetryMutation = useMutation({
    mutationFn: (ids: string[]) => adminApi.bulkRetryUploads(ids),
    onSuccess: (result) => {
      toast.success(`Retry initiated for ${result.success} uploads`);
      if (result.failed > 0) {
        toast.error(`Failed to retry ${result.failed} uploads`);
      }
      setSelectedUploads(new Set());
      queryClient.invalidateQueries({ queryKey: ['failed-uploads'] });
      queryClient.invalidateQueries({ queryKey: ['uploads'] });
    },
    onError: () => {
      toast.error('Failed to initiate bulk retry');
    },
  });

  const handleRetry = async (uploadId: string) => {
    try {
      await adminApi.retryUpload(uploadId);
      toast.success('Upload retry initiated');
      queryClient.invalidateQueries({ queryKey: ['failed-uploads'] });
      queryClient.invalidateQueries({ queryKey: ['uploads'] });
    } catch (error) {
      toast.error('Failed to retry upload');
    }
  };

  const handleDelete = async (uploadId: string) => {
    if (!confirm('Are you sure you want to delete this failed upload?')) return;
    
    try {
      await adminApi.deleteUpload(uploadId);
      toast.success('Upload deleted');
      queryClient.invalidateQueries({ queryKey: ['failed-uploads'] });
    } catch (error) {
      toast.error('Failed to delete upload');
    }
  };

  const handleSelectAll = () => {
    if (selectedUploads.size === uploadsData?.items.length) {
      setSelectedUploads(new Set());
    } else {
      setSelectedUploads(new Set(uploadsData?.items.map(upload => upload.id) || []));
    }
  };

  const handleSelectUpload = (uploadId: string) => {
    const newSelected = new Set(selectedUploads);
    if (newSelected.has(uploadId)) {
      newSelected.delete(uploadId);
    } else {
      newSelected.add(uploadId);
    }
    setSelectedUploads(newSelected);
  };

  const handleBulkRetry = () => {
    if (selectedUploads.size === 0) return;
    bulkRetryMutation.mutate(Array.from(selectedUploads));
  };

  const handleBulkDelete = async () => {
    if (selectedUploads.size === 0) return;
    
    if (!confirm(`Are you sure you want to delete ${selectedUploads.size} failed uploads?`)) return;

    try {
      const deletePromises = Array.from(selectedUploads).map(id => adminApi.deleteUpload(id));
      await Promise.all(deletePromises);
      toast.success(`Deleted ${selectedUploads.size} uploads`);
      setSelectedUploads(new Set());
      queryClient.invalidateQueries({ queryKey: ['failed-uploads'] });
    } catch (error) {
      toast.error('Failed to delete some uploads');
    }
  };

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to load data</h3>
        <p className="text-gray-500 mb-4">There was an error loading the failed uploads.</p>
        <button
          onClick={() => queryClient.invalidateQueries({ queryKey: ['failed-uploads'] })}
          className="btn-primary"
        >
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Failed Uploads
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Review and manage uploads that failed to process
          </p>
        </div>
        <div className="mt-4 flex space-x-3 md:mt-0 md:ml-4">
          <button
            onClick={() => queryClient.invalidateQueries({ queryKey: ['failed-uploads'] })}
            className="btn-outline"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        <div className="card p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="bg-red-500 rounded-md p-3">
                <AlertTriangle className="h-6 w-6 text-white" />
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Total Failed</dt>
                <dd className="text-lg font-semibold text-gray-900">{uploadsData?.total || 0}</dd>
              </dl>
            </div>
          </div>
        </div>
        
        <div className="card p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="bg-blue-500 rounded-md p-3">
                <RefreshCw className="h-6 w-6 text-white" />
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Can Retry</dt>
                <dd className="text-lg font-semibold text-gray-900">
                  {uploadsData?.items.filter(u => u.retryCount < u.maxRetries).length || 0}
                </dd>
              </dl>
            </div>
          </div>
        </div>
        
        <div className="card p-5">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="bg-gray-500 rounded-md p-3">
                <Trash2 className="h-6 w-6 text-white" />
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">Max Retries</dt>
                <dd className="text-lg font-semibold text-gray-900">
                  {uploadsData?.items.filter(u => u.retryCount >= u.maxRetries).length || 0}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Bulk Actions */}
      <div className="card p-6">
        <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 sm:items-center">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search failed uploads by filename, user, or error message..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="input pl-10"
              />
            </div>
          </div>
          
          {/* Bulk Actions */}
          {selectedUploads.size > 0 && (
            <div className="flex space-x-2">
              <button
                onClick={handleBulkRetry}
                disabled={bulkRetryMutation.isPending}
                className="btn-primary"
              >
                <RefreshCw className={clsx('h-4 w-4 mr-2', bulkRetryMutation.isPending && 'animate-spin')} />
                Retry Selected ({selectedUploads.size})
              </button>
              <button
                onClick={handleBulkDelete}
                className="btn-danger"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Failed Uploads Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left">
                  <button
                    onClick={handleSelectAll}
                    className="flex items-center space-x-2 text-gray-500 hover:text-gray-700"
                  >
                    {selectedUploads.size === uploadsData?.items.length && uploadsData?.items.length > 0 ? (
                      <CheckSquare className="h-4 w-4" />
                    ) : (
                      <Square className="h-4 w-4" />
                    )}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  File Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Error
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Retries
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Failed At
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <AnimatePresence>
                {uploadsData?.items.map((upload) => (
                  <FailedUploadRow
                    key={upload.id}
                    upload={upload}
                    isSelected={selectedUploads.has(upload.id)}
                    onSelect={handleSelectUpload}
                    onRetry={handleRetry}
                    onDelete={handleDelete}
                  />
                ))}
              </AnimatePresence>
            </tbody>
          </table>
        </div>

        {isLoading && (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-twitch-600" />
          </div>
        )}

        {!isLoading && (!uploadsData?.items || uploadsData.items.length === 0) && (
          <div className="text-center py-12">
            <CheckSquare className="h-12 w-12 text-green-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No failed uploads</h3>
            <p className="text-gray-500">
              {searchTerm 
                ? 'No failed uploads match your search criteria'
                : 'Great! All uploads are processing successfully'
              }
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {uploadsData && uploadsData.total > 20 && (
        <div className="flex items-center justify-between bg-white px-4 py-3 border border-gray-200 rounded-lg">
          <div className="flex items-center">
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{(currentPage - 1) * 20 + 1}</span> to{' '}
              <span className="font-medium">
                {Math.min(currentPage * 20, uploadsData.total)}
              </span>{' '}
              of <span className="font-medium">{uploadsData.total}</span> failed uploads
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={!uploadsData.hasPrev}
              className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4" />
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={!uploadsData.hasNext}
              className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

interface FailedUploadRowProps {
  upload: UploadJob;
  isSelected: boolean;
  onSelect: (id: string) => void;
  onRetry: (id: string) => void;
  onDelete: (id: string) => void;
}

const FailedUploadRow: React.FC<FailedUploadRowProps> = ({ 
  upload, 
  isSelected, 
  onSelect, 
  onRetry, 
  onDelete 
}) => {
  const canRetry = upload.retryCount < upload.maxRetries;
  
  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <motion.tr
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className={clsx(
        'hover:bg-gray-50 transition-colors',
        isSelected && 'bg-blue-50'
      )}
    >
      <td className="px-6 py-4 whitespace-nowrap">
        <button
          onClick={() => onSelect(upload.id)}
          className="flex items-center space-x-2 text-gray-500 hover:text-gray-700"
        >
          {isSelected ? (
            <CheckSquare className="h-4 w-4 text-blue-600" />
          ) : (
            <Square className="h-4 w-4" />
          )}
        </button>
      </td>
      
      <td className="px-6 py-4">
        <div className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center">
              <FileText className="h-5 w-5 text-red-600" />
            </div>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-900 truncate max-w-lg">
              {upload.filename}
            </p>
            <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
              <span className="flex items-center">
                <User className="h-3 w-3 mr-1" />
                User ID: {upload.userId}
              </span>
              <span>{formatBytes(upload.originalSize)}</span>
              {upload.metadata.streamTitle && (
                <span className="truncate max-w-xs" title={upload.metadata.streamTitle}>
                  "{upload.metadata.streamTitle}"
                </span>
              )}
            </div>
          </div>
        </div>
      </td>
      
      <td className="px-6 py-4">
        <div className="max-w-xs">
          <p className="text-sm text-red-800 font-medium mb-1">
            Error Details
          </p>
          <p className="text-xs text-red-600 break-words" title={upload.errorMessage}>
            {upload.errorMessage || 'Unknown error occurred'}
          </p>
        </div>
      </td>
      
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm">
          <span className={clsx(
            'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
            canRetry 
              ? 'bg-yellow-100 text-yellow-800' 
              : 'bg-red-100 text-red-800'
          )}>
            {upload.retryCount} / {upload.maxRetries}
          </span>
          <div className="text-xs text-gray-500 mt-1">
            {canRetry ? 'Can retry' : 'Max retries reached'}
          </div>
        </div>
      </td>
      
      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
        <div className="flex items-center">
          <Calendar className="h-4 w-4 mr-1" />
          <div>
            <div>{new Date(upload.startedAt).toLocaleDateString()}</div>
            <div className="text-xs">{new Date(upload.startedAt).toLocaleTimeString()}</div>
          </div>
        </div>
      </td>
      
      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
        <div className="flex items-center justify-end space-x-2">
          {canRetry && (
            <button
              onClick={() => onRetry(upload.id)}
              className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-100"
              title="Retry upload"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          )}
          <button
            onClick={() => onDelete(upload.id)}
            className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-100"
            title="Delete upload"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </td>
    </motion.tr>
  );
};

export default FailedUploads;